<template>
  <footer id="footer" class="def-footer focus-inverted">
    <div class="footer-top">
      <section
        v-if="$config?.FOOTER?.company?.enabled"
        class="footer-section footer-section--company"
        aria-labelledby="footer-contact-heading"
      >
        <h5 id="footer-contact-heading">{{ $t('footer.heading.contact') }}</h5>
        <p v-html="$config.FOOTER.company.html" />
      </section>

      <section
        id="social-block"
        v-if="socialLinks.length"
        class="footer-section footer-section--social"
        aria-labelledby="footer-social-heading"
      >
        <h5 id="footer-social-heading">{{ $t('footer.heading.socialMedia') }}</h5>
        <div class="social-icons">
          <a
            v-for="link in socialLinks"
            :key="link.key"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="capitalizeFirstLetter(link.key)"
            :class="link.key"
          >
            <font-awesome-icon :icon="link.icon" aria-hidden="true" />
          </a>
        </div>
      </section>
      <section id="newsletter-block" class="footer-section footer-section--newsletter">
        <div
          v-if="$config?.FOOTER?.newsletter?.enabled"
          aria-labelledby="footer-newsletter-heading"
        >
          <h5 id="footer-newsletter-heading">{{ $t('footer.heading.newsletter') }}</h5>
          <a :href="$config.FOOTER.newsletter.url" target="_blank" rel="noopener noreferrer" class="link">{{ $t('footer.label.newsletterSubscribe') }}</a>
        </div>
      </section>
    </div>

    <hr class="footer-separator" />

    <div class="footer-bottom">
      <div>
        <div>&copy; {{ companyName }} {{ currentYear }} (v4.2.0)</div>
        <!-- todo PR - Better Region Name!
              aria-label muss für alle sprachen gehen. ABER emojis sind nicht für Braile. Dafür gibt es "aria-braillelabel".
              Aufgabe: finde ein passenden text für ALLE SPRACHEN, da dies vorgelesen wird für Englisch, Französisch und Deutsch.
        -->
        <!--
       @ Accessibility
       aria-label for a Region names the region; region is of type Landmark and can be quickly jumped to.
        -->
        <div class="footer-language-switcher d-flex flex-column" role="region" :aria-label="languageRegionLabel">
          <Button class="sr-only sr-only-focusable" v-for="entry in langList()" :key="entry.value" @click="changeLocale(entry.value)" v-bind:aria-pressed="isLanguageActive(entry.value)?'true':'false'" :aria-label="entry.nativeSelectText" :lang="entry.title" preset-size>{{entry.nativeSelectText}}</Button>
        </div>
      </div>

      <!--
       @ Accessibility
       * This Element is a Timer showing the time until Session Expiration.
       * The heading is there, to make the timer easier to find from the heading list instead of searching the page for the
      -->
      <div role="heading" aria-level="5" v-if="$config.SHOW_HEADER_LOGOUT_TIMER && activeTime > 0" v-bind:aria-label="timeRemaining"><div role="timer" aria-live="off">{{ timeRemaining }}</div></div>

      <!--
       @ Accessibility
       * The nav Element is of role="navigation". Use it sparingly!
       * Each Nav should have a unique aria-label to distiguish them, except if they have the same content.
      -->
      <nav class="bottom-links" :aria-label="$t('footer.label.ariaRegionName')">
        <a v-for="l in bottomLinks" :key="l.Key" :href="l.Url" target="_blank">{{ $t(l.Text) }}</a>
      </nav>
    </div>
  </footer>
</template>

<script>
import { SessionTimer } from "@/store/SessionTimer";
import {capitalizeFirstLetter} from "@/utils/utils";
import Button from "@/components/actions/Button.vue";
import {useLocaleStore} from "@/stores/locale";

const SOCIAL_ICONS = {
  facebook: ['fab', 'facebook-f'],
  instagram: ['fab', 'instagram'],
  twitter: ['fab', 'twitter'],
  linkedin: ['fab', 'linkedin-in'],
  youtube: ['fab', 'youtube'],
  tiktok: ['fab', 'tiktok'],
  pinterest: ['fab', 'pinterest']
};

export default {
  name: 'Footer',
  components: {Button},
  methods: {
    capitalizeFirstLetter,
    async changeLocale(locale){
      await this.localeStore.setLocale(locale);
    },
    langList(){
      return this.$config?.LANGUAGE_LIST?.filter(function(l){return !this.isLanguageActive(l.value);},this);
    },
    isLanguageActive(language) {
      return this.$i18n.locale === language;
    }
  },
  data() {
    return {
      timer: undefined,
      localeStore: useLocaleStore()
    };
  },
  props: {
    links: { type: Array, default: () => [] }, // legacy/fallback
  },
  computed: {
    socialLinks() {
      const social = this.$config?.FOOTER?.social || {};
      return Object.entries(social)
        .filter(([, url]) => !!url)
        .map(([key, url]) => ({
          key,
          url,
          icon: SOCIAL_ICONS[key] || null,
        }))
        .filter(link => link.icon);
    },
    currentYear() { return new Date().getFullYear(); },
    languageRegionLabel() {
      if (typeof this.$te === 'function' && this.$te('footer.label.languageSelection')) {
        return this.$t('footer.label.languageSelection');
      }
      return 'Language selection';
    },
    companyName() {
      return this.$config?.FOOTER?.copyright?.enabled ? this.$config.FOOTER.copyright.label : undefined;
    },
    bottomLinks() {
      return this.$config?.FOOTER?.links?.length ? this.$config.FOOTER.links : (this.links || []);
    },
    activeTime() { // is cached, but updates once "this.timer" or "this.timer.activeTimer" changes
      if (this.timer === undefined)
        return 0;
      return this.timer.activeTimer;
    },
    timeRemaining(){
      if (this.activeTime > 0) {
        var timeString = "";
        let minutesLeft = this.activeTime / 60000;
        if (minutesLeft > 1) {
          timeString = Math.round(minutesLeft) + " min.";
        } else {
          timeString = "< 1 min.";
        }
        return this.$t('text.timeRemaining') + "" + timeString;
      }
      return "";
    },
  },
  created() {
    this.timer = SessionTimer();
    this.timer.onMount();
  },
  beforeDestroy() {
    this.timer?.onUnmount();
  }
}
</script>

<style scoped lang="scss">
.footer-top {
  display:flex;
  flex-wrap:wrap;
  justify-content: space-between;
}

.social-icons {
  display:flex;
  flex-wrap:wrap;
  gap: var(--footer-social-gap, 18px);
}

.footer-bottom {
  display:flex;
  flex-wrap:wrap;
  gap:8px 16px;
  justify-content:space-between;
  align-items:center;
}
.bottom-links {
  display: flex;
  gap: 16px;
  padding: 0;
  flex-wrap: wrap;
}

@media (max-width: 900px) {
  .footer-top {
    gap: 12px;
  }
  .bottom-links {
    gap:24px;
    flex-wrap: wrap;
  }
  .social-icons a {
    font-size:18px;
  }
}
@media (max-width:600px) {
  .footer-bottom {
    flex-direction:column;
    align-items:flex-start;
  }
  .bottom-links {
    width: 100%;
    gap: 12px;
    flex-wrap: wrap;
  }
}
</style>
